<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import type { Dentist, DentistEdit, OpenMode } from "@/types";
import { dentist, diff } from "@/types";
import { computed, ref, inject, watch } from "vue";
import { MDBTextarea } from "mdb-vue-ui-kit";
import FormInput from "@/components/FormInput.vue";
import { useRouter } from "vue-router";
import { dentistsPathName } from "@/router";
import { mutateDentistKey } from "@/logic/provide";

const router = useRouter();
const props = defineProps<{
  modelValue: Dentist;
  openMode: OpenMode;
  minified?: boolean;
}>();
const editDentist = inject(mutateDentistKey);

const minified = ref(props.minified || false);
const editMode = ref(false);
const model = ref(dentist(props.modelValue)); // local copy

const showDetails = ref(false);

const emit = defineEmits<{
  "click:detail": [element: Dentist, offset: number];
  "click:remove": [element: Dentist];
}>();

function clickSelect(target: EventTarget | null) {
  if (!target) {
    return;
  }

  var me = target as HTMLElement;

  // Trigger a scroll into view when selected
  if (props.openMode === "Edit") {
    setTimeout(() => {
      me.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }

  while (!me.classList.contains("prct-container") && me.offsetParent) {
    me = me.offsetParent as HTMLElement;
  }

  emit("click:detail", model.value, me.offsetTop);
}

function toggleEdit() {
  editMode.value = !editMode.value;
}

function cancel() {
  model.value = dentist(props.modelValue);
  editMode.value = false;
}

function save() {
  if (editDentist) {
    const patch: DentistEdit = diff(props.modelValue, model.value);
    if (Object.keys(patch).length !== 0) {
      editDentist(props.modelValue.id, patch);
    }
  }
  editMode.value = false;
}

function onRemove() {
  emit("click:remove", model.value);
}

function onOpen() {
  router.push({ name: dentistsPathName, params: { id: props.modelValue.id } });
}

function toggleShowDetails() {
  showDetails.value = !showDetails.value;
  if (showDetails.value) {
    setTimeout(() => {
      showDetails.value = false;
    }, 600);
  }
}

const headerId = computed(() => {
  let pre = "";
  if (model.value.dep_id && model.value.dep_id.length > 1) {
    pre += "(" + model.value.dep_id + ") ";
  }
  if (model.value.cdo_id) {
    return pre + model.value.cdo_id;
  } else {
    return pre + "??";
  }
});

const computed_contact = computed(() => ({
  email:
    model.value.contact.email && model.value.contact_imported.email
      ? { value: model.value.contact.email, source: "multi" }
      : model.value.contact.email
      ? { value: model.value.contact.email, source: "" }
      : { value: model.value.contact_imported.email, source: "4D" },
  phone_landline:
    model.value.contact.phone_landline &&
    model.value.contact_imported.phone_landline
      ? { value: model.value.contact.phone_landline, source: "multi" }
      : model.value.contact.phone_landline
      ? { value: model.value.contact.phone_landline, source: "" }
      : {
          value: model.value.contact_imported.phone_landline,
          source: "4D",
        },
  phone_cellular:
    model.value.contact.phone_cellular &&
    model.value.contact_imported.phone_cellular
      ? { value: model.value.contact.phone_cellular, source: "multi" }
      : model.value.contact.phone_cellular
      ? { value: model.value.contact.phone_cellular, source: "" }
      : {
          value: model.value.contact_imported.phone_cellular,
          source: "4D",
        },

  address:
    model.value.contact.address && model.value.contact_imported.address
      ? { value: model.value.contact.address, source: "multi" }
      : model.value.contact.address
      ? { value: model.value.contact.address, source: "" }
      : { value: model.value.contact_imported.address, source: "4D" },
  city:
    model.value.contact.address && model.value.contact_imported.address
      ? { value: model.value.contact.city, source: "multi" }
      : model.value.contact.city
      ? { value: model.value.contact.city, source: "" }
      : { value: model.value.contact_imported.city, source: "4D" },
  zipcode:
    model.value.contact.address && model.value.contact_imported.address
      ? { value: model.value.contact.zipcode, source: "multi" }
      : model.value.contact.zipcode
      ? { value: model.value.contact.zipcode, source: "" }
      : { value: model.value.contact_imported.zipcode, source: "4D" },
}));

watch(props, () => {
  minified.value = props.minified || false;
});
</script>

<template>
  <div class="prct-container">
    <div
      class="header-container"
      :class="{ pointer: openMode === 'Edit' }"
      @click.stop="clickSelect($event.target)"
    >
      <div class="d-header">
        <div class="spaceme">
          <img src="@/assets/icons/id-card-bold.svg" class="icon" />
          <h2 class="l-name">{{ model.last_name }}</h2>
          <span class="colored input-header-text">{{ model.first_name }}</span>
        </div>
        <h3>
          {{ headerId }}
        </h3>
      </div>
    </div>
    <div class="content-container" @click.stop="toggleShowDetails">
      <template v-if="editMode">
        <div class="crow">
          <!-- Name -->
          <div>
            <FormInput
              label="Nom"
              type="text"
              v-model="model.last_name"
              :disabled="!editMode"
            />
          </div>
          <div>
            <FormInput
              label="Prénom"
              type="text"
              v-model="model.first_name"
              :disabled="!editMode"
            />
          </div>
          <!-- Ids -->
          <div style="width: 46px">
            <FormInput
              label="Dep."
              type="text"
              v-model="model.dep_id"
              :disabled="!editMode"
            />
          </div>
          <div style="width: 70px">
            <FormInput
              label="CDO"
              type="number"
              v-model="model.cdo_id"
              :disabled="!editMode"
            />
          </div>
        </div>
      </template>
      <div class="two-cols-split">
        <div class="crow">
          <!-- Editable ids -->
          <div style="width: 80px">
            <FormInput
              label="CNO"
              type="number"
              v-model="model.cno_id"
              :disabled="true"
            />
          </div>
          <div style="width: 128px">
            <FormInput
              label="Date de naissance"
              type="text"
              v-model="model.date_of_birth"
              :disabled="true"
            />
          </div>
        </div>
        <div class="crow appart">
          <div style="width: 122px">
            <FormInput
              label="RPPS"
              type="number"
              v-model="model.rpps_id"
              :disabled="true"
            />
          </div>
          <div style="width: 120px">
            <FormInput
              label="Nb. dossiers"
              type="number"
              :model-value="model.cases.length"
              :disabled="true"
              v-if="!editMode"
            />
          </div>
        </div>
      </div>
      <!-- Address -->
      <template v-if="minified"> </template>
      <template v-else-if="!editMode && !showDetails">
        <!-- Merged -->
        <div class="two-cols-split">
          <div class="crow v-space">
            <FormInput
              label="Adresse"
              type="text"
              v-model="computed_contact.address.value"
              :footer="computed_contact.address.source"
              :disabled="!editMode"
            />
          </div>
          <div class="crow v-space">
            <div style="width: 70px">
              <FormInput
                label=""
                type="text"
                v-model="computed_contact.zipcode.value"
                :footer="computed_contact.zipcode.source"
                :disabled="!editMode"
              />
            </div>
            <div>
              <FormInput
                label=""
                type="text"
                v-model="computed_contact.city.value"
                :footer="computed_contact.city.source"
                :disabled="!editMode"
              />
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <!-- Edit 4D for info -->
        <div class="two-cols-split">
          <div class="crow v-space">
            <FormInput
              label="Adresse"
              type="text"
              v-model="model.contact_imported.address"
              footer="4D"
              disabled="true"
            />
          </div>
          <div class="crow v-space">
            <div style="width: 70px">
              <FormInput
                label=""
                type="text"
                v-model="model.contact_imported.zipcode"
                footer="4D"
                :disabled="true"
              />
            </div>
            <div>
              <FormInput
                label=""
                type="text"
                v-model="model.contact_imported.city"
                footer="4D"
                :disabled="true"
              />
            </div>
          </div>
        </div>
        <div class="two-cols-split">
          <!-- Edit (Local) -->
          <div class="crow">
            <FormInput
              label="Adresse"
              type="text"
              v-model="model.contact.address"
              :disabled="!editMode"
            />
          </div>
          <div class="crow">
            <div style="width: 70px">
              <FormInput
                label=""
                type="text"
                v-model="model.contact.zipcode"
                :disabled="!editMode"
              />
            </div>
            <div>
              <FormInput
                label=""
                type="text"
                v-model="model.contact.city"
                :disabled="!editMode"
              />
            </div>
          </div>
        </div>
      </template>
      <!-- Contact -->
      <template v-if="minified"> </template>
      <template v-else-if="!editMode && !showDetails">
        <div class="two-cols-split">
          <div class="crow v-space">
            <FormInput
              label="Email"
              type="email"
              v-model="computed_contact.email.value"
              :footer="computed_contact.email.source"
              :disabled="!editMode"
            />
          </div>
          <div class="crow v-space">
            <div style="width: 130px">
              <FormInput
                label="Tel. Fixe"
                type="tel"
                v-model="computed_contact.phone_landline.value"
                :footer="computed_contact.phone_landline.source"
                :disabled="!editMode"
              />
            </div>
            <div style="width: 130px">
              <FormInput
                label="Tel. Mobile"
                type="tel"
                v-model="computed_contact.phone_cellular.value"
                :footer="computed_contact.phone_cellular.source"
                :disabled="!editMode"
              />
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <!-- Edit 4D contact -->
        <div class="two-cols-split">
          <div class="crow v-space">
            <FormInput
              label="Email"
              type="email"
              v-model="model.contact_imported.email"
              footer="4D"
              disabled="true"
            />
          </div>
          <div class="crow v-space">
            <div style="width: 130px">
              <FormInput
                label="Tel. Fixe"
                type="tel"
                v-model="model.contact_imported.phone_landline"
                footer="4D"
                disabled="true"
              />
            </div>
            <div style="width: 130px">
              <FormInput
                label="Tel. Mobile"
                type="tel"
                v-model="model.contact_imported.phone_cellular"
                footer="4D"
                disabled="true"
              />
            </div>
          </div>
        </div>
        <!-- Edit Local Contact -->
        <div class="two-cols-split">
          <div class="crow">
            <FormInput
              label="Email"
              type="email"
              v-model="model.contact.email"
              :disabled="!editMode"
            />
          </div>
          <div class="crow">
            <div style="width: 130px">
              <FormInput
                label="Tel. Fixe"
                type="tel"
                v-model="model.contact.phone_landline"
                :disabled="!editMode"
              />
            </div>
            <div style="width: 130px">
              <FormInput
                label="Tel. Mobile"
                type="tel"
                v-model="model.contact.phone_cellular"
                :disabled="!editMode"
              />
            </div>
          </div>
        </div>
      </template>

      <div class="crow v-space" v-if="!minified">
        <MDBTextarea
          label="Notes"
          rows="2"
          v-model="model.notes"
          :disabled="!editMode"
        />
      </div>
    </div>
    <div class="content-footer" v-if="!minified">
      <div v-if="editMode" class="buttons">
        <span class="button" @click.stop="cancel()">{{ "Annuler" }}</span>
        <span class="button" @click.stop="save()">{{ "Enregistrer" }}</span>
      </div>
      <div v-else-if="openMode != 'ReadOnly'" class="buttons">
        <span
          v-if="openMode == 'InlineList'"
          class="button"
          @click.stop="onRemove()"
          >{{ "Retirer" }}</span
        >
        <span class="button" @click.stop="toggleEdit()">{{ "Editer" }}</span>
        <span
          v-if="openMode == 'InlineList'"
          class="button"
          @click.stop="onOpen()"
          >{{ "Voir les dossiers" }}</span
        >
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
h2 {
  display: inline-block;
  padding-right: 8px;
}

.entry {
  text-decoration: underline;
}

h2,
h3,
.colored {
  color: var(--vt-c-purple);
  &.no-color {
    color: inherit;
  }
}

h2.l-name,
h3.l-name {
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nowrap {
  white-space: nowrap;
}

.pointer {
  cursor: pointer;
}

.prct-container {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 680px;
  min-height: 40px;

  border-radius: 0 6px 6px 0;

  background-color: var(--color-background-soft);
  transition: all 0.1s ease-in-out;

  &.list:hover,
  &.selected {
    transition: transform 0.2s ease-in;
    transform: translateX(12px);
  }

  .header-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 56px;

    border-width: var(--cdo-border-thin-width);
    border-bottom-style: solid;
    border-color: var(--vt-c-purple);

    white-space: nowrap;

    .d-header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      width: 100%;
      margin-left: 1em;
      margin-right: 1em;
    }

    .spaceme {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: baseline;

      margin-right: 1em;
      flex-wrap: wrap;
    }
  }

  .content-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 1.5em 1em 1em 1em;
    row-gap: 1.3em;

    .two-cols-split {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: space-evenly;
      column-gap: 1em;
    }

    .crow {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      flex-wrap: nowrap;
      column-gap: 1em;
      width: 100%;

      &.appart {
        justify-content: space-between;
      }

      &.v-space {
        margin-top: 1em;
      }
    }
  }

  .content-footer {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    font-style: normal;
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;
    margin-left: 1em;
    margin-bottom: 1.25em;

    .buttons {
      span {
        margin-left: 1em;
      }
    }

    div:last-child {
      margin-right: 1em;
    }
  }
}
</style>
